import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { openWhatsApp, openCalendarEvent } from '@/lib/contact';
import { Plus, Search, Grid3X3, List, MessageSquare, Calendar, Mail, ChevronRight } from 'lucide-react';
import TemplateSelect from '@/components/TemplateSelect';

function formatINR(n) { if (!n) return '0'; const num = Number(n); if (num >= 100000) return `${(num/100000).toFixed(1)}L`; return num.toLocaleString('en-IN'); }

const phases = ['Lead', 'Consultation', 'Design', 'Execution', 'Handover'];
const statusBadge = { Active: 'bg-[#3A8C8C]/10 text-[#3A8C8C]', 'On Hold': 'bg-[#C67D5B]/10 text-[#C67D5B]', Completed: 'bg-[#7DA68A]/10 text-[#7DA68A]', Archived: 'bg-[#E8E6E1] text-[#6B7280]' };

function PhaseBar({ phase }) {
  const idx = phases.indexOf(phase);
  return (
    <div className="flex gap-1 items-center">
      {phases.map((p, i) => (
        <div key={p} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= idx ? 'bg-[#3A8C8C]' : 'bg-[#E8E6E1]'}`} />
      ))}
    </div>
  );
}

export default function ProjectsPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [showCreate, setShowCreate] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQ, setSearchQ] = useState('');
  const [form, setForm] = useState({ name: '', client_name: '', client_email: '', client_phone: '', project_type: 'Residential', budget: 0, start_date: '', end_date: '' });
  const [useTemplate, setUseTemplate] = useState('no');
  const [templateId, setTemplateId] = useState('');

  const loadProjects = async () => {
    try { const res = await api.get('/projects'); setProjects(res.data); }
    catch { toast.error('Failed to load projects'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadProjects(); }, []);

  const handleCreate = async () => {
    try {
      const payload = { ...form };
      if (useTemplate === 'yes' && templateId) payload.template_id = templateId;
      const res = await api.post('/projects', payload);
      toast.success(payload.template_id ? 'Project created with template BOQ' : 'Project created');
      setShowCreate(false);
      setForm({ name: '', client_name: '', client_email: '', client_phone: '', project_type: 'Residential', budget: 0, start_date: '', end_date: '' });
      setUseTemplate('no'); setTemplateId('');
      navigate(`/projects/${res.data.id}`);
    } catch { toast.error('Failed to create project'); }
  };

  const filtered = projects.filter(p => {
    if (filterStatus !== 'all' && p.status !== filterStatus) return false;
    if (searchQ && !p.name.toLowerCase().includes(searchQ.toLowerCase()) && !p.client_name.toLowerCase().includes(searchQ.toLowerCase())) return false;
    return true;
  });

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#3A8C8C] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="projects-page">
      <div className="section-heading-row flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-['Manrope'] font-bold">Projects</h1>
          <p className="text-sm text-[#6B7280] mt-0.5">{projects.length} projects</p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <button data-testid="create-project-btn" className="bg-[#3A8C8C] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#2E7A7A] transition-colors flex items-center gap-2 active:scale-95">
              <Plus size={16} strokeWidth={1.5} /> New Project
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-['Manrope']">New Project</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <div><Label className="text-xs text-[#6B7280] uppercase tracking-wide">Project Name</Label><Input data-testid="project-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" placeholder="e.g. Villa Serenity" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-[#6B7280] uppercase tracking-wide">Client Name</Label><Input data-testid="project-client-input" value={form.client_name} onChange={e => setForm({...form, client_name: e.target.value})} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
                <div><Label className="text-xs text-[#6B7280] uppercase tracking-wide">Type</Label>
                  <Select value={form.project_type} onValueChange={v => setForm({...form, project_type: v})}><SelectTrigger className="mt-1 h-10 rounded-xl border-[#D4D0C8]"><SelectValue /></SelectTrigger><SelectContent>{['Residential','Commercial','Retail','Other'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-[#6B7280] uppercase tracking-wide">Budget (INR)</Label><Input type="number" value={form.budget} onChange={e => setForm({...form, budget: Number(e.target.value)})} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
                <div><Label className="text-xs text-[#6B7280] uppercase tracking-wide">Client Email</Label><Input type="email" value={form.client_email} onChange={e => setForm({...form, client_email: e.target.value})} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-[#6B7280] uppercase tracking-wide">Start Date</Label><Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
                <div><Label className="text-xs text-[#6B7280] uppercase tracking-wide">End Date</Label><Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
              </div>
              {/* Start From Template */}
              <div className="rounded-xl border border-[#E8E6E1] p-3 bg-[#FAFAF8]/50">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-[#6B7280] uppercase tracking-wide">Start From Template?</Label>
                  <div className="flex gap-1 bg-white rounded-lg p-0.5 border border-[#E8E6E1]">
                    {['no', 'yes'].map(opt => (
                      <button key={opt} type="button" onClick={() => { setUseTemplate(opt); if (opt === 'no') setTemplateId(''); }}
                        data-testid={`use-template-${opt}`}
                        className={`px-3 py-1 rounded-md text-xs font-medium capitalize transition-colors ${useTemplate === opt ? 'bg-[#3A8C8C] text-white' : 'text-[#6B7280] hover:text-[#2C2C2E]'}`}>
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
                {useTemplate === 'yes' && (
                  <div className="mt-3">
                    <TemplateSelect value={templateId} onChange={(id) => setTemplateId(id)} />
                    <p className="text-[11px] text-[#6B7280] mt-2">The project's BOQ will be pre-filled with this template's rooms &amp; items, using current Item &amp; Cost Sheet rates.</p>
                  </div>
                )}
              </div>
              <button onClick={handleCreate} data-testid="submit-project-btn" className="w-full h-10 bg-[#3A8C8C] text-white rounded-full font-medium text-sm hover:bg-[#2E7A7A] transition-colors">Create Project</button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1 bg-[#E8E6E1]/50 rounded-xl p-1">
          {['all', 'Active', 'On Hold', 'Completed'].map(s => (
            <button key={s} onClick={() => setFilterStatus(s)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filterStatus === s ? 'bg-white text-[#2C2C2E] shadow-sm' : 'text-[#6B7280] hover:text-[#2C2C2E]'}`}>
              {s === 'all' ? 'All' : s}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search size={16} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <Input placeholder="Search projects..." value={searchQ} onChange={e => setSearchQ(e.target.value)} className="pl-9 h-9 rounded-xl border-[#E8E6E1] bg-white" data-testid="projects-search" />
        </div>
        <div className="flex gap-1 bg-[#E8E6E1]/50 rounded-xl p-1">
          <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded-lg ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}><Grid3X3 size={16} strokeWidth={1.5} /></button>
          <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-lg ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}><List size={16} strokeWidth={1.5} /></button>
        </div>
      </div>

      {/* Projects */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-grid">
          {filtered.map(project => {
            const daysLeft = project.end_date ? Math.ceil((new Date(project.end_date) - Date.now()) / 86400000) : null;
            return (
              <div key={project.id} onClick={() => navigate(`/projects/${project.id}`)} className="glass-card rounded-2xl p-5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 cursor-pointer group" data-testid={`project-card-${project.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-[#2C2C2E] text-sm font-['Manrope'] group-hover:text-[#3A8C8C] transition-colors">{project.name}</h3>
                    <p className="text-xs text-[#6B7280] mt-0.5">{project.client_name}</p>
                  </div>
                  <Badge className={`text-[10px] font-normal ${statusBadge[project.status] || ''}`}>{project.status}</Badge>
                </div>
                <PhaseBar phase={project.phase} />
                <div className="flex items-center justify-between mt-3 text-xs">
                  <span className="text-[#6B7280]">{project.phase} &middot; {formatINR(project.budget)}</span>
                  {daysLeft !== null && <span className={`font-medium ${daysLeft < 0 ? 'text-[#C4727F]' : daysLeft < 30 ? 'text-[#C67D5B]' : 'text-[#7DA68A]'}`}>{daysLeft < 0 ? `${Math.abs(daysLeft)}d over` : `${daysLeft}d`}</span>}
                </div>
                <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-[#E8E6E1] opacity-0 group-hover:opacity-100 transition-opacity">
                  <Badge className="bg-[#E8E6E1] text-[#2C2C2E] text-[10px] font-normal">{project.project_type}</Badge>
                  <div className="flex-1" />
                  <button className="p-1 rounded-md hover:bg-[#E8E6E1]" title="WhatsApp client"
                    onClick={e => { e.stopPropagation(); if (!openWhatsApp(project.client_phone, `Hi ${project.client_name},`)) toast.error('No client phone on this project'); }}
                  ><MessageSquare size={13} strokeWidth={1.5} className="text-[#6B7280]" /></button>
                  <button className="p-1 rounded-md hover:bg-[#E8E6E1]" title="Schedule meeting"
                    onClick={e => { e.stopPropagation(); openCalendarEvent({ title: `${project.name} — meeting`, details: `Client: ${project.client_name}` }); }}
                  ><Calendar size={13} strokeWidth={1.5} className="text-[#6B7280]" /></button>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && <p className="col-span-full text-center text-sm text-[#6B7280] py-12">No projects found</p>}
        </div>
      ) : (
        <div className="glass-card rounded-2xl overflow-hidden" data-testid="projects-list">
          {filtered.map(project => (
            <div key={project.id} onClick={() => navigate(`/projects/${project.id}`)} className="flex items-center gap-4 px-5 py-4 border-b border-[#E8E6E1] last:border-0 hover:bg-[#FAFAF8] transition-colors cursor-pointer" data-testid={`project-row-${project.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-medium text-[#2C2C2E]">{project.name}</h3>
                  <Badge className={`text-[10px] font-normal ${statusBadge[project.status] || ''}`}>{project.status}</Badge>
                </div>
                <p className="text-xs text-[#6B7280] mt-0.5">{project.client_name} &middot; {project.project_type}</p>
              </div>
              <div className="w-32"><PhaseBar phase={project.phase} /></div>
              <span className="text-sm text-[#2C2C2E] font-medium w-20 text-right">{formatINR(project.budget)}</span>
              <ChevronRight size={16} strokeWidth={1.5} className="text-[#6B7280]" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
